<template>
    <kgr-timeline :as-preview="asPreview">
        <kgr-timeline-item
            v-for="(transition, index) in currentTransitions"
            :key="index"
            :pull-left="index % 2 === 0"
            :as-preview="asPreview"
        >
            <template #icon>
                <kgr-timeline-item-icon
                    :in-future="transition.isInFuture()"
                    :title="$t(`child_contract_status.${transition.targetStatus}`)"
                    :status="transition.targetStatus"
                />
            </template>

            <template #content>
                <div
                    class="status_element status_name"
                    :class="{ status_disabled: transition.isInFuture() }"
                >
                    {{ $t(`child_contract_status_transition.${transition.name}`) }}
                    <kgr-icon-delete
                        v-if="editMode && index === currentTransitions.length - 1"
                        @click="remove(transition.id)"
                    />
                </div>
                <div
                    class="status_element status_date"
                    :class="{ status_disabled: transition.isInFuture() }"
                >
                    <kgr-timeline-item-date
                        v-if="editMode"
                        :value="moment(transition.date).format('DD.MM.YYYY')"
                        show-on-init
                        :edit-mode="currentDateEditMode === transition.id"
                        :is-invalid="currentDateIsInvalid === transition.id"
                        @clickToEdit="currentDateEditMode = transition.id"
                        @blur="currentDateIsInvalid = null"
                        @change="updateTransition(transition, $event)"
                    />
                    <div v-else>
                        {{ moment(transition.date).format('DD.MM.YYYY') }}
                    </div>
                </div>
            </template>
        </kgr-timeline-item>

        <div v-if="editMode">
            <kgr-timeline-item
                v-show="addMode && (currentTransitions.length === 0 || currentTransitions[currentTransitions.length - 1].targetStatus !== 'archived')"
                :pull-left="currentTransitions.length % 2 === 0"
            >
                <template #icon>
                    <kgr-timeline-item-icon
                        v-if="loading || newTransition"
                        :spinning="loading"
                        :title="newTransition ? $t(`child_contract_status.${newTransition?.targetStatus}`) : ''"
                        :status="newTransition ? newTransition?.targetStatus : null"
                    />
                </template>

                <template #content>
                    <kgr-possible-status-transition-data-provider
                        v-if="!forceButtonReload"
                        :child-id="childId"
                        :callback="stopLoading"
                    >
                        <template #default="scope">
                            <div
                                v-if="!loading"
                                class="animated fadeIn"
                            >
                                <div class="status_element status_name">
                                    {{ newTransitionLabel }}
                                </div>

                                <div class="new-transition-elem">
                                    <kgr-timeline-item-date
                                        v-model="newTransitionDate"
                                        edit-mode
                                        :is-invalid="newDateIsInvalid"
                                    />
                                </div>

                                <kgr-transition-button
                                    v-for="(transition, index) in scope.data"
                                    :key="index"
                                    class="transition_selection"
                                    :status="transition.targetStatus"
                                    @mouseover.native="newTransition = transition"
                                    @mouseout.native="newTransition = null"
                                    @click.native="addTransition(transition, scope.reload)"
                                />
                            </div>
                        </template>
                    </kgr-possible-status-transition-data-provider>
                </template>
            </kgr-timeline-item>
            <div
                v-if="!addMode && (currentTransitions.length === 0 || currentTransitions[currentTransitions.length - 1].targetStatus !== 'archived')"
                class="new_transition_button"
            >
                <kgr-button @click="addMode = true">
                    {{ $t('child_contract_status.addNewTransition') }}
                </kgr-button>
            </div>
        </div>
    </kgr-timeline>
</template>

<script>
import ChildStatusTransitionRepository from '../../../js/repository/ChildStatusTransitionRepository.js';
import IconDelete from '../../common/icon/IconDelete.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import PossibleStatusTransitionDataProvider from '../../data-provider/PossibleStatusTransitionDataProvider.vue';
import Timeline from '../../common/timeline/Timeline.vue';
import TimelineItem from '../../common/timeline/TimelineItem.vue';
import TimelineItemDate from '../../common/timeline/TimelineItemDate.vue';
import TimelineItemIcon from '../../common/timeline/TimelineItemIcon.vue';
import TransitionButton from './TransitionButton.vue';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-timeline': Timeline,
        'kgr-timeline-item': TimelineItem,
        'kgr-timeline-item-icon': TimelineItemIcon,
        'kgr-timeline-item-date': TimelineItemDate,
        'kgr-transition-button': TransitionButton,
        'kgr-icon-delete': IconDelete,
        'kgr-possible-status-transition-data-provider': PossibleStatusTransitionDataProvider,
    },
    props: {
        childId: {
            type: Number,
            required: true,
        },
        transitions: {
            type: Array,
            required: true,
            default: () => [],
        },
        reload: {
            type: Function,
            required: true,
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        asPreview: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            currentTransitions: [...this.transitions],
            loading: true,
            forceButtonReload: false,
            currentDateEditMode: null,
            currentDateIsInvalid: null,
            newDateIsInvalid: false,
            newTransitionDate: '',
            newTransition: null,
            now: this.moment(),
            addMode: false,
        };
    },
    computed: {
        newTransitionLabel() {
            return this.newTransition === null ? this.$t('pleaseChooseAnOption') : this.$t(`child_contract_status_transition.${this.newTransition.name}`);
        },
    },
    watch: {
        transitions: {
            immediate: true,
            deep: true,
            handler() {
                this.newTransitionDate = this.now.format('DD.MM.YYYY');

                this.transitions.forEach((transition) => {
                    if (this.now <= transition.date) {
                        this.newTransitionDate = transition.date.format('DD.MM.YYYY');
                    }
                });

                this.currentTransitions = [...this.transitions];
            },
        },
    },
    methods: {
        remove(id) {
            showConfirm(this.$t('confirm'), this.$t('child_contract_status.alert.confirmDelete'), () => {
                this.loading = true;
                this.currentTransitions.splice(this.currentTransitions.length - 1, 1);
                this.addMode = false;
                ChildStatusTransitionRepository
                    .remove(id)
                    .then(() => this.reload().then(() => {
                        this.newTransition = null;
                        this.forceButtonReload = true;
                        this.$nextTick(() => this.forceButtonReload = false);
                    }))
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, 'Ok');
        },
        addTransition(transition, reloadPossibleTransitions) {
            const date = this.moment(this.newTransitionDate, 'DD.MM.YYYY');

            this.loading = true;
            ChildStatusTransitionRepository
                .create(this.childId, transition, date)
                .then(() => {
                    this.newDateIsInvalid = false;
                    this.reload().then(() => {
                        this.addMode = false;
                        reloadPossibleTransitions().then(() => this.loading = false);
                    });
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error('child_contract_status.alert.dateFailure');
                        this.newDateIsInvalid = true;
                    } else {
                        this.$notify.error('saveChanges.error');
                    }

                    this.loading = false;
                });
        },
        updateTransition(transition, date) {
            if (date !== '') {
                date = this.moment(date, 'DD.MM.YYYY');
                ChildStatusTransitionRepository
                    .update(transition.id, date)
                    .then(() => {
                        transition.date = date;
                        this.currentDateEditMode = null;
                        this.$notify.success('saveChanges.success');
                        this.reload();
                    })
                    .catch((error) => {
                        if (error.status === 409) {
                            this.$notify.error('child_contract_status.alert.dateFailure');
                            this.currentDateIsInvalid = transition.id;
                        } else if (error.details && error.details.length > 0) {
                            this.$notify.error(error.details[0].constraintMessage);
                        } else {
                            this.$notify.error('saveChanges.error');
                        }
                    },
                    );
            }
        },
        stopLoading() {
            this.loading = false;
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
    .status_name {
    font-size: var(--kgr-font-size-text);
    font-weight: var(--kgr-font-weight-bold);
}

.status_date {
    font-size: calc(0.867 * var(--kgr-font-size-text));
    display: inline-block;
}

.status_disabled {
    color: #cbcbcb;
}

.new-transition-elem {
    margin-top: 5px;
}

.transition_selection {
    cursor: pointer;
    display: inline-block;
    &:hover {
        transform: scale(1.2);
    }
    &.timeline_item_dot {
        background-color: transparent;
    }
}

.new_transition_button {
    width: 100%;
    background: white;
    position: relative;
    padding-top: 8px;
    button {
        margin: 0 auto;
        display: block;
    }
}
</style>
